// color-exempt-file — retokenisation Phase 6 ui-themes : dark:bg-[#0a0a0a] preserve le rendu dark historique (voir memoire ui-themes)
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "@core/lib/supabase";
import { resolveAuthReturnTo, takeAuthReturnTo } from "@core/lib/authReturnTo";

/**
 * Page de callback OAuth — gère le retour après authentification Google/SSO.
 * Supabase v2 traite automatiquement le code PKCE via onAuthStateChange,
 * ce composant attend la session puis redirige.
 *
 * LA DESTINATION EST CHERCHÉE À TROIS ENDROITS, ET C'EST NÉCESSAIRE.
 *
 * `signInWithGoogle` la place dans la chaîne de requête (`?returnTo=`), mais
 * ce paramètre peut ne jamais revenir : si l'URL de redirection ne correspond
 * pas exactement à la liste blanche du projet Supabase, Supabase retombe sur
 * la Site URL — sans aucune chaîne de requête, et sans erreur. On atterrit
 * alors sur l'accueil, et l'utilisateur doit rouvrir son e-mail et recliquer.
 *
 * Or la destination est AUSSI dans `localStorage`, posée par `AuthRequired`.
 * Voir `@core/lib/authReturnTo` pour le détail des trois lectures et pourquoi
 * la normalisation (retrait du basename `/date`) se fait à l'écriture.
 *
 * _Vécu le 21/09/2026 : le lien de désinscription `/data-control` d'un e-mail,
 * suivi d'une connexion Google, ramenait sur la page d'accueil._
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const { t } = useTranslation("polls");

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(window.location.hash.substring(1));

    // takeAuthReturnTo() efface le stockage une seule fois, ici, après lecture.
    const returnTo = resolveAuthReturnTo(
      [
        queryParams.get("returnTo"),
        hashParams.get("returnTo"),
        takeAuthReturnTo(),
      ],
      "/",
    );

    const doRedirect = (sub: { unsubscribe: () => void }) => {
      sub.unsubscribe();
      navigate(returnTo, { replace: true });
    };

    // Supabase v2 PKCE : échanger le code contre une session
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_IN" && session) {
        doRedirect(subscription);
      }
    });

    // Fallback : si déjà connecté, rediriger immédiatement
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        doRedirect(subscription);
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  return (
    <div className="min-h-screen-safe bg-background dark:bg-[#0a0a0a] flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto mb-4" />
        <p className="text-muted-foreground text-sm">
          {t("authCallback.connecting")}
        </p>
      </div>
    </div>
  );
}
